import type { UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import Entity from "_framework/entities/entity";
import Guid from "_shared/guids/guid";
import BookmarkedCompany from "marketplace/bookmarks/company-bookmarks/bookmarked-company";
import BookmarkedCompanyListItemSchema, {
  BookmarkedCompanyListItemJSON,
  BookmarkedCompanyListItemParams,
} from "marketplace/bookmarks/company-bookmarks/_schema/bookmarked-company-list-item-schema";
import Company from "marketplace/companies/company";

/**
 * One row of the caller's company bookmarks: the bookmark, and the bookmarked company when the
 * caller may read it. A row without the company is still a bookmark that the caller can delete.
 */
export default class BookmarkedCompanyListItem
  extends Entity<BookmarkedCompanyListItem, BookmarkedCompanyListItemJSON, BookmarkedCompanyListItemParams> {
  public static create(values: BookmarkedCompanyListItemParams): BookmarkedCompanyListItem {
    const parsed = BookmarkedCompanyListItemSchema.validate(values);
    return new BookmarkedCompanyListItem(parsed);
  }

  public static fromJSON(json: BookmarkedCompanyListItemJSON): BookmarkedCompanyListItem {
    const parsed = BookmarkedCompanyListItemSchema.validateJSON(json);
    return new BookmarkedCompanyListItem({
      bookmark: BookmarkedCompany.fromJSON({
        id: parsed.id,
        createdDate: parsed.createdDate,
        companyId: parsed.companyId,
      }),
      company: parsed.company ? Company.fromJSON(parsed.company) : undefined,
      unavailableReason: parsed.unavailableReason ?? undefined,
    });
  }

  public static fromJSONArray(jsonArray: BookmarkedCompanyListItemJSON[]): BookmarkedCompanyListItem[] {
    return jsonArray.map((json) => BookmarkedCompanyListItem.fromJSON(json));
  }

  public readonly bookmark: BookmarkedCompany;
  public readonly company?: Company;
  public readonly unavailableReason?: Extract<UnavailableReason, "unauthorized">;

  /** The bookmark's id, so a page of rows can be keyed like any other page. */
  public get id(): Guid {
    return this.bookmark.id;
  }

  public get companyId(): Guid {
    return this.bookmark.companyId;
  }

  /** True when the caller may not read the bookmarked company. */
  public get isUnavailable(): boolean {
    return this.unavailableReason !== undefined;
  }

  public constructor(params: BookmarkedCompanyListItemParams) {
    super(params);
    this.bookmark = params.bookmark;
    this.company = params.company;
    this.unavailableReason = params.unavailableReason;
  }

  public toJSON(): BookmarkedCompanyListItemJSON {
    return {
      ...this.bookmark.toJSON(),
      company: this.company?.toJSON() ?? null,
      unavailableReason: this.unavailableReason ?? null,
    };
  }

  public valueOf(): BookmarkedCompanyListItemJSON {
    return this.toJSON();
  }

  public toString(): string {
    return `Bookmarked Company List Item: ${this.bookmark.id.toString()} (${this.unavailableReason ?? "visible"})`;
  }

  public isEqualTo(other: BookmarkedCompanyListItem | null | undefined): boolean {
    if (!other) return false;
    const sameCompany = this.company && other.company
      ? this.company.isEqualTo(other.company)
      : !this.company && !other.company;
    return this.bookmark.isEqualTo(other.bookmark) && this.unavailableReason === other.unavailableReason && sameCompany;
  }
}
